import type { PropertyFilterOption } from '@cloudscape-design/collection-hooks';
import { useMemo } from 'react';

import { useGetTypes } from '../config';
import { useGetRibbonItemVisibilities } from './useGetRibbonItemVisibilities';

/**
 * Vocabulary of entity Type labels that can appear on the My Items ribbon,
 * scoped to the types the current user can actually see — entities whose
 * module is disabled or the user lacks permission for are omitted so they
 * don't show up as filter options pointing at rows that'll never match.
 *
 * Without this, the previous `Set(ribbonItems.map(r => r.Type))` approach
 * dropped any type from the dropdown as soon as it had no matching rows,
 * which meant a saved `Type = Document` token couldn't be re-selected on
 * users with no assigned documents (RSP-4427).
 */
export const useGetTypeFilterOptions = (): PropertyFilterOption[] => {
  const types = useGetTypes();
  const visibilities = useGetRibbonItemVisibilities();

  return useMemo(() => {
    const gated: (false | string | undefined)[] = [
      visibilities.risks && types.risk,
      visibilities.actions && types.action,
      visibilities.assessments && types.assessment,
      visibilities.controls && types.control,
      visibilities.issues && types.issue,
      visibilities.rcsa && types.RCSAActivity,
      visibilities.policies && types.document,
      visibilities.indicators && types.indicator,
      visibilities.obligations && types.obligationReview,
    ];

    return gated
      .filter((label): label is string => Boolean(label))
      .map((label) => ({
        propertyKey: 'Type',
        value: label,
        label,
      }));
  }, [types, visibilities]);
};
